import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useLogin } from '@/hooks/use-api';
import { loginSchema, type LoginFormData } from '@/lib/validations';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import logo from '/onextel-logo-white.png';

const LoginPage: React.FC = () => {
  const location = useLocation();
  const loginMutation = useLogin();
  const from = (location.state as any)?.from?.pathname || '/dashboard';
  console.log('Login page - redirect path:', from);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema) as any,
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginFormData) => {
    toast.loading('Signing in...', { id: 'login-toast' });
    try {
      await loginMutation.mutateAsync({
        email: values.email,
        password: values.password,
      });
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.data?.errors) {
        toast.dismiss('login-toast');
        error.response.data.errors.forEach((err: any) => {
          form.setError(err.field as keyof LoginFormData, {
            message: err.message,
          });
        });
      } else {
        toast.error(error.response?.data?.message || 'Login failed', {
          id: 'login-toast',
        });
      }
    }
  };

  return (
    <main className="login-bg-container">
      <section className="dark:bg-main-dark-bg relative">
        <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
          <div className="w-full md:mt-0 max-w-screen-lg">
            <Card className="glassy-card flex flex-row w-full">
              {/* Left branding panel */}
              <div className="w-full md:w-1/2 hidden md:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-red-500 to-red-600 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-400 rounded-full opacity-20 -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-red-400 rounded-full opacity-20 -ml-12 -mb-12"></div>
                <div className="flex flex-col justify-center items-center text-center z-10 max-w-sm">
                  <img className="w-32 mb-8" src={logo} alt="onextel-logo" />
                  <h1 className="text-white font-bold text-3xl mb-4">
                    Real-time, effortless conversations.
                  </h1>
                  <p className="text-white/90 text-sm mb-8 leading-relaxed">
                    Powering intelligent SMS campaigns that drive meaningful
                    customer connections and measurable results.
                  </p>
                  <div className="space-y-4 w-full">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                          <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                        </svg>
                      </div>
                      <span className="text-sm">Omnichannel messaging</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                      <span className="text-sm">AI-powered campaigns</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                        </svg>
                      </div>
                      <span className="text-sm">Real-time analytics</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right form panel */}
              <div className="w-full md:w-1/2">
                <div className="auth-form-content">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Welcome back
                    </h2>
                    <p className="text-sm text-gray-600">
                      Sign in to your Onextel - Optimove account
                    </p>
                  </div>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">Email</FormLabel>
                            <FormControl>
                              <Input placeholder="you@company.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">
                              Password
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                placeholder="Enter your password"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        className="w-full bg-red-500 hover:bg-[#23203f] text-white font-medium"
                        type="submit"
                      >
                        Sign in
                      </Button>
                      <div className="auth-divider">or</div>
                      <div className="flex flex-col space-y-3">
                        <Link className="w-full" to="/forgot-password">
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-600 hover:bg-[#23203f] hover:text-white hover:border-[#23203f] transition-colors"
                          >
                            Forgot Password?
                          </Button>
                        </Link>
                        <Link className="w-full" to="/register">
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-600 hover:bg-[#23203f] hover:text-white hover:border-[#23203f] transition-colors"
                          >
                            Create Account
                          </Button>
                        </Link>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
